/**
 * 전역 confirm/notify 다이얼로그 — `window.confirm` / `alert` 대체.
 *
 * <h3>왜 만들었나</h3>
 * <p>브라우저 native {@code window.confirm} 은 OS 별로 모양이 다르고 페이지 URL 이 헤더에 노출됨
 * (스크린샷의 "localhost:3000 내용:" 등). 디자인 시스템과 분리되어 톤이 깨져 보임.
 * 사용자 요청 (2026-05-05) 으로 자체 모달로 통일.
 *
 * <h3>사용법 — Promise 기반 imperative API</h3>
 * <pre>
 *   import { confirm } from "../../util/confirmDialog";
 *
 *   const ok = await confirm({
 *       title:        "정말 삭제할까요?",
 *       message:      "이 메일을 휴지통으로 이동합니다.",
 *       confirmLabel: "삭제",
 *       cancelLabel:  "취소",
 *       variant:      "danger",   // 기본 "default"
 *   });
 *   if (!ok) return;
 *   // 진행
 * </pre>
 *
 * <h3>동작 원리</h3>
 * <p>{@link ConfirmDialogHost} 가 앱 루트에 한 번만 마운트. 외부에서 {@link confirm} 호출 시
 * 내부 emitter 가 host 의 state 를 갱신해 다이얼로그를 표시. 사용자 클릭 결과를 Promise 로 resolve.
 *
 * <h3>여러 confirm 동시 호출</h3>
 * <p>큐 방식 — 첫 호출 결과 resolve 될 때까지 다음 호출은 대기. UX 단순함 우선.
 * <p>단, <b>방금 띄운 것과 같은 내용</b>의 재호출은 큐에 넣지 않고 취소(false)로 즉시 응답한다
 * (더블클릭 잔여 타 — {@link _isEchoOf} 참고).
 */
import React, { useEffect, useState } from "react";
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import { ds } from "../styles/designSystem";
import ActionButton from "../component/common/ActionButton";

/* ── 외부 → host 통신 emitter (singleton) ─────────────────────────── */
let _resolveCurrent = null;       // 현재 진행 중 confirm 의 resolver
let _setHostState = null;         // host 의 setState — host 마운트 시 등록
const _queue = [];                // 동시 호출 대기 큐
let _currentOpts = null;          // 진행 중 항목의 opts — 중복(메아리) 호출 판별용
let _currentShownAt = 0;          // 진행 중 항목을 띄운 시각(ms)

/**
 * 외부 사용 진입점 — Promise<boolean> 반환.
 *
 * @param {object} opts
 * @param {string} [opts.title]         제목 (없으면 message 만 표시)
 * @param {string} opts.message         본문 메시지 (필수). 줄바꿈 \n 허용.
 * @param {string} [opts.confirmLabel]  확인 버튼 라벨 (기본 "확인")
 * @param {string} [opts.cancelLabel]   취소 버튼 라벨 (기본 "취소")
 * @param {"default"|"danger"} [opts.variant]  "danger" 면 확인 버튼 빨강
 * @returns {Promise<boolean>} 확인=true, 취소/X=false
 */
export function confirm(opts) {
    return new Promise((resolve) => {
        const item = { opts: { variant: "default", ...opts }, resolve };
        if (_resolveCurrent) {
            // 방금 띄운 것과 같은 내용이면 큐잉이 아니라 무시한다 — 사용자는 한 번만 물어봤다고 여긴다.
            // 더블클릭 2타가 (배드드롭이 아니라) 아직 살아 있는 트리거 버튼에 닿으면 confirm 이 두 번 호출된다.
            // 큐에 넣으면 답한 뒤 똑같은 다이얼로그가 한 번 더 뜨고, 그것까지 확인하면 삭제·발송이 2회 나간다
            // (호출부 대부분이 멱등이 아니다). 아직 사용자가 안 눌렀을 뿐이므로 결과는 취소(false) —
            // 호출부의 `if (!ok) return;` 으로 조용히 빠진다.
            if (_isEchoOf(item.opts)) { resolve(false); return; }
            // 내용이 다르면 진짜 별개의 질문 — 원래대로 큐에 enqueue 해 하나씩 순차 표시한다.
            _queue.push(item);
            return;
        }
        _showItem(item);
    });
}

/**
 * 방금 띄운 것과 <b>같은 질문</b>의 재호출인가 — 유예 시간 안이면 더블클릭 잔여 타로 본다.
 * 유예를 배드드롭과 같은 값으로 두는 이유는 원인이 같기 때문이다(2타가 어디에 떨어지느냐만 다르다).
 *
 * <p>다른 대상에 대한 같은 문구까지 삼킬 위험은 없다 — 다이얼로그가 모달이라 떠 있는 동안에는
 * 사용자가 다른 행/버튼을 누를 수 없다. 유예 안에 같은 질문이 또 들어왔다면 같은 연타뿐이다.
 */
function _isEchoOf(opts) {
    if (!_currentOpts || Date.now() - _currentShownAt >= BACKDROP_GRACE_MS) return false;
    return ["title", "message", "confirmLabel", "cancelLabel", "variant"]
        .every((k) => opts[k] === _currentOpts[k]);
}

/**
 * 진행 중인 confirm 을 <b>사용자 대신 취소</b>로 닫는다(대기 큐도 비운다).
 *
 * <p>물어본 이유 자체가 사라졌을 때 쓴다 — 예를 들어 "세션을 연장할까요?" 를 띄워 둔 채
 * 세션이 그냥 만료돼 로그인 화면으로 넘어가면, 답할 대상이 없는 모달만 위에 떠 있게 된다.
 * 사용자가 아직 안 눌렀을 뿐이므로 결과는 취소(false)로 본다.
 */
export function dismissConfirm() {
    // 대기 중이던 항목도 하나씩 resolve(false) 한 뒤 버린다. 그냥 비우면 그 `await confirm(...)` 이
    // 영구 미해결로 남아 뒷 코드가 영영 실행되지 않는다(await 뒤의 정리 로직까지 통째로 사라진다).
    // 결과가 false 인 이유는 위와 같다 — 사용자가 아직 안 눌렀을 뿐이므로 취소로 본다.
    _queue.splice(0).forEach((item) => item.resolve(false));
    _resolveCurrent?.(false);
}

/** 닫힘 애니메이션(transitionDuration 120ms)이 끝난 뒤 큐의 다음 항목을 띄우기 위한 간격. */
const QUEUE_GAP_MS = 160;

function _showItem(item) {
    _resolveCurrent = (result) => {
        item.resolve(result);
        _resolveCurrent = null;
        _currentOpts = null;
        // 큐가 남아 있어도 현재 것은 반드시 닫는다. 예전엔 다음 항목이 있으면 open:false 를 건너뛰어,
        // 답을 했는데도 똑같이 생긴 다이얼로그가 화면에 그대로 남아 있었다(다음 항목이 그 자리를 이어받는 꼴).
        // opts 는 남긴다 — 120ms 페이드아웃 동안 제목·본문이 사라지고 버튼이 기본 라벨로 바뀌지 않게.
        _setHostState?.((prev) => ({ ...prev, open: false }));
        // 순차 표시는 그대로 유지 — 닫힘 애니메이션이 끝난 뒤에 다음 것을 연다.
        const next = _queue.shift();
        if (next) setTimeout(() => _showItem(next), QUEUE_GAP_MS);
    };
    _currentOpts = item.opts;
    _currentShownAt = Date.now();
    _setHostState?.({ open: true, opts: item.opts });
}

/* ── 두 호스트 공통 — 배드드롭/ESC 닫힘 규칙 ──────────────────────── */

/**
 * 다이얼로그가 뜬 직후 이 시간(ms) 안에 들어온 배드드롭 클릭은 무시한다.
 *
 * <p>확인 다이얼로그는 트리거 버튼의 click 핸들러 안에서 ~10ms 만에 마운트된다. 그래서 버튼을
 * <b>더블클릭</b>(또는 두 번 빠르게 클릭)하면 2타가 방금 깔린 배드드롭 위에 떨어지고,
 * 사용자는 다이얼로그를 보지도 못한 채 취소(false)로 resolve 된다 —
 * "작성 폐기를 눌렀는데 작성창이 안 닫힌다"가 정확히 이 경로였다(에러도 안 남아 조용히 실패).
 *
 * <p>400ms 는 OS 더블클릭 간격(보통 ≤500ms)의 2타를 걸러내면서, 사람이 다이얼로그를 읽고
 * 바깥을 누르기까지 걸리는 시간보다는 짧다. 의도한 바깥 클릭 취소는 그대로 동작한다.
 */
const BACKDROP_GRACE_MS = 400;

/**
 * 배드드롭/ESC 로 닫아도 되는지 판단 — confirm·notify 두 호스트가 같은 규칙을 쓰도록 한 곳에 모았다.
 * 한쪽만 고쳐 두면 다음 사람이 어느 쪽이 맞는 규칙인지 알 수 없다.
 *
 * <p>MUI 는 배드드롭 클릭과 ESC 를 모두 onClose 로 보낸다 — reason 을 봐야 위 레이스를 거를 수 있다.
 *
 * @returns {(reason: string) => boolean} 닫아도 되면 true
 */
function useDismissGuard(open, opts) {
    const shownAtRef = React.useRef(0);
    // opts 는 _showItem 마다 새 객체 — 큐가 이어서 띄우는 다음 항목도 유예를 새로 받는다.
    useEffect(() => { if (open) shownAtRef.current = Date.now(); }, [open, opts]);
    return (reason) =>
        !(reason === "backdropClick" && Date.now() - shownAtRef.current < BACKDROP_GRACE_MS);
}

/* ─────────────────────────────────────────────────────────────────────
 * confirmNotify() — alert 대체. confirm() 과 같은 imperative API, 확인 버튼 하나만.
 * 사용처에서 import { confirmNotify } from "../../util/confirmDialog" 한 줄만 추가하면 됨.
 * NotifyDialogHost 는 confirm 의 host 와 함께 index.js 에 1회 마운트.
 *
 * ⚠ util/toast.js 의 notify() 와 혼동 금지 — 이름이 같아 화면마다 토스트/모달이
 *   갈라지는 문제가 있어 이쪽을 confirmNotify 로 개명했다(2026-07-23).
 *   · notify(msg, type)        → 상단 토스트. 응답 없음. 단순 알림은 전부 이쪽.
 *   · confirmNotify({message}) → 모달. 사용자가 "확인" 눌러야 Promise resolve.
 *     흐름을 반드시 멈춰야 할 때(로그아웃 직전 안내 등)만 쓴다.
 * ──────────────────────────────────────────────────────────────────── */
let _resolveNotify = null;
let _setNotifyState = null;
const _notifyQueue = [];

/**
 * 전역 알림 모달 — 사용자가 "확인" 을 눌러야 Promise 가 resolve 된다.
 * 단순 알림은 util/toast.js 의 notify() 를 쓰고, 여기는 응답을 받아야 할 때만.
 *
 * @param {object} opts
 * @param {string} [opts.title]            제목
 * @param {string} opts.message            본문 (필수)
 * @param {string} [opts.confirmLabel]     확인 버튼 라벨 (기본 "확인")
 * @param {"info"|"success"|"warning"|"error"} [opts.severity]  색상 톤
 * @returns {Promise<void>} 확인 클릭 시 resolve
 */
export function confirmNotify(opts) {
    return new Promise((resolve) => {
        const item = { opts: { severity: "info", ...opts }, resolve };
        if (_resolveNotify) { _notifyQueue.push(item); return; }
        _showNotifyItem(item);
    });
}

function _showNotifyItem(item) {
    _resolveNotify = () => {
        item.resolve();
        _resolveNotify = null;
        const next = _notifyQueue.shift();
        if (next) setTimeout(() => _showNotifyItem(next), 80);
        else _setNotifyState?.({ open: false });
    };
    _setNotifyState?.({ open: true, opts: item.opts });
}

const severityColor = (s) => {
    switch (s) {
        case "error":   return "#dc2626";
        case "warning": return "#ca8a04";
        case "success": return "#16a34a";
        default:        return "#0f172a";
    }
};

export function NotifyDialogHost() {
    const [{ open, opts }, setState] = useState({ open: false, opts: {} });
    const shouldClose = useDismissGuard(open, opts);
    useEffect(() => {
        _setNotifyState = setState;
        return () => { _setNotifyState = null; _resolveNotify = null; };
    }, []);
    const handleOk = () => _resolveNotify?.();
    // confirm 쪽과 같은 규칙 — 여긴 «확인» 하나뿐이라 배드드롭 클릭이 곧 확인으로 처리된다.
    // 피해는 작지만(취소가 없으니 결과가 뒤집히지 않는다) 뜬 직후 잔여 타로 알림이 사라지면
    // 사용자는 읽지도 못한 채 넘어간다. 규칙이 갈리지 않게 여기서도 유예를 건다.
    const handleClose = (_event, reason) => { if (shouldClose(reason)) handleOk(); };
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: "12px", minWidth: 360, maxWidth: 480,
                    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
                },
            }}
            transitionDuration={120}
        >
            {opts?.title && (
                <DialogTitle sx={{
                    fontSize: 15, fontWeight: 800,
                    color: severityColor(opts.severity),
                    pb: 1, pt: 2.5, px: 3,
                }}>
                    {opts.title}
                </DialogTitle>
            )}
            <DialogContent sx={{ px: 3, pt: opts?.title ? 0 : 3, pb: 1 }}>
                <Typography sx={{
                    fontSize: 13.5, color: "#334155", lineHeight: 1.6,
                    whiteSpace: "pre-line",
                }}>
                    {opts?.message || ""}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                <ActionButton
                    variant="confirm"
                    onClick={handleOk}
                    autoFocus
                    startIcon={false}
                    label={opts?.confirmLabel || "확인"}
                    sx={{ minWidth: 80 }}
                />
            </DialogActions>
        </Dialog>
    );
}

/* ── 호스트 컴포넌트 — 앱 루트에 1번만 마운트 ────────────────────── */

export function ConfirmDialogHost() {
    const [{ open, opts }, setState] = useState({ open: false, opts: {} });
    const shouldClose = useDismissGuard(open, opts);

    useEffect(() => {
        _setHostState = setState;
        return () => {
            _setHostState = null;
            _resolveCurrent = null;
        };
    }, []);

    const handleConfirm = () => _resolveCurrent?.(true);
    const handleCancel  = () => _resolveCurrent?.(false);

    const handleClose = (_event, reason) => { if (shouldClose(reason)) handleCancel(); };

    const isDanger = opts?.variant === "danger";
    const title    = opts?.title;
    const message  = opts?.message || "";
    const confirmLabel = opts?.confirmLabel || "확인";
    const cancelLabel  = opts?.cancelLabel  || "취소";

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            // 다이얼로그 등장은 커서 위치와 상관없이 항상 화면 중앙 — 안정적 UX.
            // ESC / 바깥 클릭 시 cancel 처리 (handleClose → handleCancel 가 false resolve).
            // 단, 뜬 직후 BACKDROP_GRACE_MS 안의 바깥 클릭은 더블클릭 잔여 타로 보고 무시한다.
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    minWidth: 360,
                    maxWidth: 480,
                    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
                },
            }}
            transitionDuration={120}
        >
            {title && (
                <DialogTitle sx={{
                    fontSize: 15, fontWeight: 700, color: "#0f172a",
                    pb: 1, pt: 2.5, px: 3,
                }}>
                    {title}
                </DialogTitle>
            )}

            <DialogContent sx={{ px: 3, pt: title ? 0 : 3, pb: 1 }}>
                <Typography sx={{
                    fontSize: 13.5, color: "#334155", lineHeight: 1.6,
                    whiteSpace: "pre-line",  // \n 줄바꿈 보존
                }}>
                    {message}
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <ActionButton
                    variant="cancel"
                    onClick={handleCancel}
                    label={cancelLabel}
                    sx={{ minWidth: 80 }}
                />
                <ActionButton
                    variant="confirm"
                    onClick={handleConfirm}
                    autoFocus
                    startIcon={false}
                    label={confirmLabel}
                    sx={{
                        minWidth: 80,
                        ...(isDanger && {
                            bgcolor: "#dc2626",
                            "&:hover": { bgcolor: "#b91c1c" },
                        }),
                    }}
                />
            </DialogActions>
        </Dialog>
    );
}
